import React, { useEffect, useState } from "react";
import TextField from "../../../../Forms/TextField";
import { useFormikContext } from "formik";
import useAxios from "axios-hooks";
import ErrorComponent from "../../../../Util/ErrorComponent"; // update path as needed

const CRS_SLUG_MAX_LENGTH = 255;

function formatStringAsSlug(string) {
    return string && string.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9\-]/g, "");
}

const BasicInfoFields = ({ courseId }) => {
    const { values, setFieldValue, setFieldTouched } = useFormikContext();
    const [showError, setShowError] = useState(false);

    // Manual request so we only validate when slug input changes.
    const [{ data: isValidSlug }, validateCourseSlug] = useAxios(
        { url: `courses/validate-course-slug`, method: "post" },
        { manual: true }
    );

    useEffect(() => {
        // Prevent stale async responses from older validations from mutating current UI state.
        let isCurrent = true;

        const validate = async () => {
            if (!values.slug) {
                // No slug yet: clear async availability/malformed banner and rely on field validation state.
                setShowError(false);
                return;
            }
            if (values.slug.length > CRS_SLUG_MAX_LENGTH) {
                // Length validation should be shown by Formik; suppress async banner to avoid duplicate/conflicting errors.
                setShowError(false);
                return;
            }
            try {
                // Server validation handles slug format and uniqueness against existing courses.
                const response = await validateCourseSlug({ data: { slug: values.slug, crsid: courseId } });
                if (!isCurrent) return;
                setShowError(!response.data); 
            } catch (err) {
                if (!isCurrent) return;
                setShowError(true);
            }
        };

        validate();
        return () => {
            // Mark this effect cycle stale so late responses are ignored.
            isCurrent = false;
        };
    }, [courseId, validateCourseSlug, values.slug]); 

    return (
        <>
            <TextField
                label="Title"
                name="title"
                onChange={(e, newValue) => {
                    // Keep slug synchronized with title edits unless user overrides it manually.
                    setFieldValue("title", newValue);
                    setFieldValue("slug", formatStringAsSlug(newValue));
                    // Mark slug touched so Formik validation feedback appears immediately.
                    setFieldTouched("slug", true, false);
                }}
            />
            <TextField
                label="Slug"
                name="slug"
                validate={(value) =>
                    value && value.length > CRS_SLUG_MAX_LENGTH
                        ? `Slug cannot contain more than ${CRS_SLUG_MAX_LENGTH} characters`
                        : undefined
                }
                description="The URL that will be used to visit the course"
            />
             {showError && (
                <ErrorComponent
                    errorMessage="Slug malformed or already in use"
                    showError={showError}
                    setShowError={setShowError}
                    isMultiline
                />
            )}
        </>
    );
};

export default BasicInfoFields;
